import {
  type AgreementArticle,
  DebtorAccessLevel,
  InvoiceState,
  type Price,
  type QueueApplicant,
  type QueueApplicantTag,
  type QueueApplicantVolition,
  type Spot,
  type Tag,
} from "$apis/parkito/index.ts";
import type { ProductKind } from "$apis/parkito2/types.gen.ts";
import type {
  AgreementRef as AgreementRef2,
  Spot as Spot2,
} from "$apis/parkito2/index.ts";
import { Toast } from "$components";
import { withApi } from "../lib/api-parkito.ts";
import {
  getVolitionType,
  volitionTypeIsFirst,
  volitionTypeIsMore,
  volitionTypeIsSwapToSpots,
  volitionTypeIsSwapToTags,
} from "./validation.ts";

const logStyle =
  "padding: 5px 2px 5px 5px; line-height: 17px; font-size: 12px; border-radius: 1px;";
export const log = {
  info: (...msg: unknown[]) => {
    const css = "color: white; background-color: blue;" + logStyle;
    if (import.meta.env.DEV) console.log("%cINFO:", css, ...msg);
  },
  err: (...msg: unknown[]) => {
    const css = "color: white; background-color: red;" + logStyle;
    if (import.meta.env.DEV) console.log("%cERROR:", css, ...msg);
  },
  warn: (...msg: unknown[]) => {
    const css = "color: orange; background-color: black;" + logStyle;
    if (import.meta.env.DEV) console.log("%cWARN:", css, ...msg);
  },
};
export function range(from: number, to: number): number[] {
  return Array(to - from).fill(from).map((x, y) => x + y);
}
export function getArticleSum(
  derivedArticles: AgreementArticle[],
  includeVat: boolean = true,
) {
  let sum = 0;
  derivedArticles.forEach((article) => {
    sum += includeVat
      ? article.price.sub + article.price.vat
      : article.price.sub;
  });
  return sum;
}

export const getPrice = (
  price: Price | null | undefined,
  withVat: boolean,
): number | undefined => {
  if (!price) {
    return undefined;
  }
  return withVat ? price.sub + price.vat : price.sub;
};

export const formatPrice = (
  price: Price | null | undefined,
  withVat: boolean = true,
): string => {
  const totalPrice = getPrice(price, withVat);
  return formatNumber(totalPrice);
};

export function transformString(str: string, len: number): string {
  if (str.length <= 10) {
    return str;
  }
  return str.slice(0, len) + "..." + str.slice(-len);
}

export function translateUserLevel(level: DebtorAccessLevel): string {
  switch (level) {
    case DebtorAccessLevel.USER:
      return "Användare";
    case DebtorAccessLevel.ADMIN:
      return "Koordinator";
    case DebtorAccessLevel.OWNER:
      return "Ägare";
    default:
      return level;
  }
}

export function formatNumber(num: number | undefined): string {
  // We always get values in Öre from backend. Then we need to divide by 100 to get SEK.
  if (num !== undefined) {
    num = num / 100;
  } else {
    return "-";
  }

  const options: Intl.NumberFormatOptions = {
    style: "decimal",
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  };

  const formatter = new Intl.NumberFormat("sv-SE", options);

  return formatter.format(num);
}
export const moneyToMajor = (minor: number | null | undefined): number => {
  if (typeof minor === "number") {
    return minor / 100;
  }
  return 0;
};

export const moneyToMinor = (major: number | null | undefined): number => {
  if (typeof major === "number") {
    return Math.round(major * 100);
  }
  return 0;
};

const openPdf = async (value: string, openInApp: boolean) => {
  const byteCharacters = atob(value);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);

  if (openInApp) {
    if (import.meta.env.DEV) console.log("Opening PDF in app");
    return "data:application/pdf;base64," + value;
  } else {
    if (import.meta.env.DEV) console.log("Opening PDF in new tab");
    return url;
  }
};

export const openInvoicePdf = async (
  debtorId: string,
  invoiceId: number,
  openInApp: boolean = false,
): Promise<string | undefined> => {
  const res = await withApi((api) => {
    return api.debtorGetInvoicePdf(
      debtorId,
      invoiceId,
    );
  });

  if (res.isOk()) {
    return openPdf(res.value, openInApp);
  } else {
    if (import.meta.env.DEV) console.log("Could not fetch invoice document");
    Toast.error("Kunde inte hämta faktura");
  }
  return undefined;
};

export const translateInvoiceState = (state: string) => {
  switch (state) {
    case InvoiceState.SENT:
      return "Skickad";
    case InvoiceState.REMINDER_SENT:
      return "Påminnelse skickad";
    case InvoiceState.CLOSED:
      return "Stängd";
    default:
      return state;
  }
};

export const translateDeliveryMethodType = (type: string) => {
  switch (type) {
    case "kivra":
      return "Kivra";
    case "mail":
      return "Post";
    case "email":
      return "E-post";
    case "sms":
      return "SMS";
    case "eInvoicePerson":
      return "E-faktura privat";
    case "eInvoiceCompany":
      return "E-faktura företag";
    default:
      return type;
  }
};

export const translatePaymentMethodType = (type: string) => {
  switch (type) {
    case "invoice":
      return "Faktura";
    case "autogiro":
      return "Autogiro";
    default:
      return type;
  }
};

export const getApplicantTagNames = (tags: Tag[], tagIds: string[]) => {
  if (tagIds.length === 0) {
    return "-";
  }
  return tags
    .filter((tag) => tagIds.includes(tag.id))
    .map((tag) => tag.name)
    .join(", ");
};

export const getSpotNames = (spots: Spot[], spotIds: string[]) => {
  if (spotIds.length === 0) {
    return "-";
  }
  return spots
    .filter((spot) => spotIds.includes(spot.id))
    .map((spot) => spot.name)
    .join(", ");
};

export const getApplicantVolitionTags = (applicant: QueueApplicant) => {
  if (volitionTypeIsFirst(applicant?.volition)) {
    return applicant?.volition.first.tags.map((tag: QueueApplicantTag) =>
      tag.tagId
    );
  } else if (volitionTypeIsMore(applicant?.volition)) {
    return applicant?.volition.more.tags.map((tag: QueueApplicantTag) =>
      tag.tagId
    );
  } else if (volitionTypeIsSwapToTags(applicant?.volition)) {
    return applicant?.volition.swapToTags.tags.map((tag: QueueApplicantTag) =>
      tag.tagId
    );
  } else {
    return [];
  }
};

export function translateProductKind(kind: ProductKind): string {
  switch (kind) {
    case "Reserved":
      return "Reserverat";
    case "Permit":
      return "Tillstånd";
    case "Other":
      return "Annat";
  }
}
export const getApplicationVolitionSpots = (applicant?: QueueApplicant) => {
  if (volitionTypeIsSwapToSpots(applicant?.volition)) {
    return applicant?.volition.swapToSpots.spotIds;
  } else {
    return [];
  }
};

export function translateVolitionType(
  volition: QueueApplicantVolition | null | undefined,
): string {
  switch (getVolitionType(volition)) {
    case "first":
      return "Erhålla ett första avtal";
    case "more":
      return "Ytterliggare avtal";
    case "swapToTags":
      return "Byta till plats med taggar";
    case "swapToSpots":
      return "Byta till specifik plats";
    default:
      return "-";
  }
}

export const createSelectItem = (
  spot: Spot2,
  tagLookup: Map<string, Tag>,
  siteId: string,
) => {
  return {
    key: spot.id,
    label: `${spot.name} - 
      ${
      spot.tagIds?.map((tagId: string) => tagLookup.get(tagId)?.name)
        .join(", ") ?? ""
    }`,
    extraLabels: [
      // Price
      formatNumber(
        spot.tagIds?.map(
          (tagId: string) => tagLookup.get(tagId)?.pricing?.price || 0,
        )
          .reduce((sum: number, price: number) => sum + price, 0),
      ) + " kr",
      // Status
      spot.relevantAgreements.find((a: AgreementRef2) => a.endDate)
        ? `Tillgänglig: ${
          spot.relevantAgreements.find((a: AgreementRef2) => a.endDate)?.endDate
        }`
        : `Ledig`,
      // URL
      `#/sites/${siteId}/spots/${spot.id}`,
    ],
    disabled: false,
  };
};
